import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TableViewIcon from '@mui/icons-material/TableView';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import PaymentIcon from 'src/assets/icons/infoTab/payment.svg';
import PlaylistAddCheckIcon from 'src/assets/icons/infoTab/sheet.svg';

import { Invoice, invoicesApi } from 'src/api/invoices';
import { Subscription, subscriptionsApi } from 'src/api/subscriptions';
import { Waitlist, waitlistsApi } from 'src/api/waitlists';
import { MaterialCircularProgress } from 'src/components/material/CircularProgress';
import styles from './Accordion.module.scss';

export const PaymentAccordion = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [waitlists, setWaitlists] = useState<Waitlist[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [invoicesRes, waitlistRes, subscriptionRes] = await Promise.all([
          invoicesApi.getInvoices(),
          waitlistsApi.getWaitlists(),
          subscriptionsApi.getSubscriptions(),
        ]);

        setInvoices(invoicesRes.result);
        setSubscriptions(subscriptionRes);
        setWaitlists(waitlistRes.result);
      } catch (err) {
        toast.error('Failed to fetch payment data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US');
  };

  const handleLeaveWaitlist = async (waitlist_id: string) => {
    try {
      await waitlistsApi.leaveWaitlist(waitlist_id);
      setWaitlists((prev) => prev.filter((item) => item._id !== waitlist_id));
      toast.success('You left the waitlist!');
    } catch (error) {
      console.error('Failed to leave waitlist:', error);
      toast.error('Failed to leave waitlist. Please try again.');
    }
  };

  const handleChangeCreditCard = (subscription_id: string) => {
    subscriptionsApi.editCreditCard(subscription_id).then((res) => {
      window.location.href = res.redirectUrl;
    });
  };

  if (loading) {
    return <MaterialCircularProgress height="60vh" size={60} />;
  }

  return (
    <Box>
      {/* Payment History */}
      <Accordion defaultExpanded sx={{ boxShadow: 'none' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <img src={PaymentIcon} alt="" />
            <Typography variant="h6">Payment history</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ boxShadow: 'none', background: 'none' }}>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Date</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Description</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Invoice</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Total</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow
                    key={invoice._id}
                    sx={{
                      borderBottom: '1px solid #eee',
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: '#fafafa' },
                    }}
                  >
                    <TableCell>{formatDate(invoice.paidAt)}</TableCell>
                    <TableCell>
                      Membership in {invoice.tribe.tribe_name}
                    </TableCell>
                    <TableCell>
                      {invoice.stripe_invoice_pdf ? (
                        <a
                          href={invoice.stripe_invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <strong>USD ${invoice.total_amount_usd / 100}</strong>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Active Subscriptions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            {/* <img src={PaymentIcon} alt="" /> */}
            <TableViewIcon />
            <Typography variant="h6">Active Subscriptions</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ boxShadow: 'none', background: 'none' }}>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Circle</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Price/Seat</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Billing Period</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Next Billing Date</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Credit Card</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions
                  .filter((sub) => sub.status === 'ACTIVE')
                  .map((sub) => (
                    <TableRow
                      key={sub._id}
                      sx={{
                        borderBottom: '1px solid #eee',
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&:hover': { backgroundColor: '#fafafa' },
                      }}
                    >
                      <TableCell>{sub.tribe.tribe_name}</TableCell>
                      <TableCell>USD ${sub.price_per_seat_usd / 100}</TableCell>
                      <TableCell>{sub.payment_period}</TableCell>
                      <TableCell>{formatDate(sub.nextBillingDate)}</TableCell>
                      <TableCell>
                        <button
                          className={styles.btn}
                          onClick={() => handleChangeCreditCard(sub._id)}
                        >
                          Change
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Waitlists */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={1}>
            <img src={PlaylistAddCheckIcon} alt="" />
            <Typography variant="h6">Waitlists</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ boxShadow: 'none', background: 'none' }}>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Waitlisted</strong>
                  </TableCell>
                  <TableCell sx={{ color: 'black' }}>
                    <strong>Name of Circle</strong>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'black' }}>
                    <strong>Get off Waitlist</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {waitlists.map((item) => (
                  <TableRow
                    key={item._id}
                    sx={{
                      borderBottom: '1px solid #eee',
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: '#fafafa' },
                    }}
                  >
                    <TableCell>{formatDate(item.createdAt)}</TableCell>
                    <TableCell>{item.tribe.tribe_name}</TableCell>
                    <TableCell align="right">
                      <button
                        className={styles.btn}
                        onClick={() => handleLeaveWaitlist(item._id)}
                      >
                        Leave
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
